"use client";

import {JSX, useState} from "react";

import {useMatchMedia} from "@/shared/hooks/dom/use-match-media";
import {useTranslation} from "@/shared/i18n";
import {Modal} from "@/shared/ui/overlay/modal";

import {GalleryImageWithSpinner} from "./gallery-image-with-spinner";
import {ImageSlider, type ImageSliderItem} from "./image-slider";

import "./styles/image-gallery-modal.scss";

export interface ImageGalleryModalProps {
    isOpen: boolean;
    images: Array<ImageSliderItem>;
    activeIndex: number;
    onClose: () => void;
    className?: string;
    virtualTourUrl?: string | null;
}

function VirtualTourIcon(): JSX.Element {
    return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

/**
 * Image gallery modal - slider on desktop, vertical scroll on mobile
 * Supports inline 3D virtual tour iframe via virtualTourUrl
 */
export function ImageGalleryModal({isOpen, images, activeIndex, onClose, className = "", virtualTourUrl}: ImageGalleryModalProps): JSX.Element {
    const isMobile = useMatchMedia({maxWidth: 768});
    const [showTour, setShowTour] = useState(false);
    const {t} = useTranslation("rental");

    const handleClose = (): void => {
        setShowTour(false);
        onClose();
    };

    const headerContent = (
        <div className="image-gallery-modal__header-actions">
            {virtualTourUrl &&
                (showTour ? (
                    <button type="button" className="image-gallery-modal__back-btn" onClick={() => setShowTour(false)}>
                        ← {t("backToPhotos")}
                    </button>
                ) : (
                    <button type="button" className="image-gallery-modal__tour-btn" onClick={() => setShowTour(true)}>
                        <VirtualTourIcon />
                        <span>{t("take3DTour")}</span>
                    </button>
                ))}
        </div>
    );

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            className={`image-gallery-modal ${className}`}
            contentClassName={`image-gallery-modal__content ${showTour ? "image-gallery-modal__content--tour" : ""}`}
            ariaLabel={showTour ? "3D Virtual Tour" : "Property images gallery"}
            buttonCloseType="primary"
            lockScroll={true}
            header={headerContent}
        >
            {showTour && virtualTourUrl ? (
                <iframe
                    src={`${virtualTourUrl}${virtualTourUrl.includes("?") ? "&" : "?"}play=1`}
                    title="3D Virtual Tour"
                    className="image-gallery-modal__tour-iframe"
                    allow="fullscreen; accelerometer; gyroscope"
                    allowFullScreen
                />
            ) : isMobile ? (
                <div className="image-gallery-modal__list">
                    {images.map((img, index) => (
                        <GalleryImageWithSpinner
                            key={`${img.src}-${index}`}
                            src={img.src}
                            alt={img.alt || `Image ${index + 1}`}
                            width={768}
                            height={512}
                            className="image-gallery-modal__list-image"
                            sizes="100vw"
                            loading={index < 3 ? "eager" : "lazy"}
                        />
                    ))}
                </div>
            ) : (
                <ImageSlider images={images} initialIndex={activeIndex} onClose={handleClose} />
            )}
        </Modal>
    );
}
